/**
 * Frontend permission catalog for the doorpin-webportal SPA.
 *
 * Mirrors nirixis-admin's authz.Catalog (Go: internal/application/authz/catalog.go)
 * exactly. Adding a permission on the backend WITHOUT adding it here means the
 * frontend can't reference it in a type-safe way from a `<Can>` guard;
 * dropping one here without also dropping it from the backend catalog means
 * dead code but no runtime breakage.
 *
 * All permission strings follow the convention:
 *
 *     <menu>.<page_or_feature>.<action>
 *
 * where <menu> is the top-level sidebar entry from menu.ts, <page_or_feature>
 * describes the page/widget/dialog, and <action> is one of the verb list from
 * the permission classification rules (view/create/update/delete/suspend/
 * activate/deactivate/revoke/publish/allocate/extend/cancel/wipe/lock/unlock/
 * notify/resend/pick/reset_password/assign_role/...).
 *
 * Scope availability (which role scopes may hold the permission) is enforced
 * on the backend — see the Scopes field on each authz.Permission entry.
 * The frontend does not enforce scope directly; the store simply contains
 * whatever list nirixis returned for the logged-in user.
 */

// ─── PermissionCode: the frozen catalog ────────────────────────────────────
//
// Kept flat so a `<Can permission={P.tenants.distributor.create}>` autocompletes
// AND so a Grep for the string "tenants.distributor.create" finds every
// callsite (including .csv docs and the backend interceptor map).
export const P = {
  dashboard: {
    device_stats_card: { view: 'dashboard.device_stats_card.view' },
    pool_availability_card: { view: 'dashboard.pool_availability_card.view' },
    upcoming_expirations_card: { view: 'dashboard.upcoming_expirations_card.view' },
    device_activity_card: { view: 'dashboard.device_activity_card.view' },
  },

  tenants: {
    distributor_list: { view: 'tenants.distributor_list.view' },
    distributor: {
      create: 'tenants.distributor.create',
      update: 'tenants.distributor.update',
      suspend: 'tenants.distributor.suspend',
      delete: 'tenants.distributor.delete',
      pick: 'tenants.distributor.pick',
    },
    reseller_list: { view: 'tenants.reseller_list.view' },
    reseller: {
      create: 'tenants.reseller.create',
      update: 'tenants.reseller.update',
      suspend: 'tenants.reseller.suspend',
      delete: 'tenants.reseller.delete',
      pick: 'tenants.reseller.pick',
    },
  },

  license_catalog: {
    product_list: { view: 'license_catalog.product_list.view' },
    product: {
      create: 'license_catalog.product.create',
      update: 'license_catalog.product.update',
      pick: 'license_catalog.product.pick',
    },
  },

  licenses: {
    distributor_pool_list: { view: 'licenses.distributor_pool_list.view' },
    distributor_pool: {
      create: 'licenses.distributor_pool.create',
      extend: 'licenses.distributor_pool.extend',
      suspend: 'licenses.distributor_pool.suspend',
      cancel: 'licenses.distributor_pool.cancel',
      pick: 'licenses.distributor_pool.pick',
    },
    reseller_allocation_list: { view: 'licenses.reseller_allocation_list.view' },
    reseller_allocation: {
      allocate: 'licenses.reseller_allocation.allocate',
      update: 'licenses.reseller_allocation.update',
      revoke: 'licenses.reseller_allocation.revoke',
    },
  },

  device_licenses: {
    list: { view: 'device_licenses.list.view' },
    device: {
      activate: 'device_licenses.device.activate',
      deactivate: 'device_licenses.device.deactivate',
      revoke: 'device_licenses.device.revoke',
      wipe: 'device_licenses.device.wipe',
    },
  },

  device_operations: {
    device_list: { view: 'device_operations.device_list.view' },
    device_detail: { view: 'device_operations.device_detail.view' },
    activity_log: { view: 'device_operations.activity_log.view' },
    command_log: { view: 'device_operations.command_log.view' },
    ping_history: { view: 'device_operations.ping_history.view' },
    command_history: { view: 'device_operations.command_history.view' },
    geolocation: { view: 'device_operations.geolocation.view' },
    device: {
      lock: 'device_operations.device.lock',
      unlock: 'device_operations.device.unlock',
      notify: 'device_operations.device.notify',
      wipe: 'device_operations.device.wipe',
      force_wipe: 'device_operations.device.force_wipe',
      delete: 'device_operations.device.delete',
    },
    command: {
      resend: 'device_operations.command.resend',
    },
  },

  reports: {
    pool_availability: { view: 'reports.pool_availability.view' },
    reseller_capacity: { view: 'reports.reseller_capacity.view' },
    expiring_devices: { view: 'reports.expiring_devices.view' },
    device_installation: {
      view: 'reports.device_installation.view',
      export: 'reports.device_installation.export',
    },
    device_lock_unlock: {
      view: 'reports.device_lock_unlock.view',
      export: 'reports.device_lock_unlock.export',
    },
  },

  identity_and_access: {
    user_list: { view: 'identity_and_access.user_list.view' },
    user_detail: { view: 'identity_and_access.user_detail.view' },
    user: {
      create: 'identity_and_access.user.create',
      update: 'identity_and_access.user.update',
      delete: 'identity_and_access.user.delete',
      assign_role: 'identity_and_access.user.assign_role',
      reset_password: 'identity_and_access.user.reset_password',
    },
    role_list: { view: 'identity_and_access.role_list.view' },
    role_detail: { view: 'identity_and_access.role_detail.view' },
    permission_tree: { view: 'identity_and_access.permission_tree.view' },
    role: {
      create: 'identity_and_access.role.create',
      update: 'identity_and_access.role.update',
      delete: 'identity_and_access.role.delete',
      pick: 'identity_and_access.role.pick',
    },
    tenant_list: { view: 'identity_and_access.tenant_list.view' },
    tenant_detail: { view: 'identity_and_access.tenant_detail.view' },
    tenant: {
      create: 'identity_and_access.tenant.create',
    },
  },
} as const

/**
 * UserScope is the tenant type the logged-in user represents. Read from the
 * `userScope` claim on the /auth/me response and used as a coarse filter for
 * UI that intrinsically doesn't make sense at a given scope (e.g. the
 * "Onboard a new distributor" button is meaningless to a DISTRIBUTOR user
 * even if some role somehow gave them the perm).
 *
 * The store already contains the correct permission list for the user's
 * effective scope — this enum is a defense-in-depth check, not a primary
 * gate.
 */
export type UserScope = 'PLATFORM' | 'DISTRIBUTOR' | 'RESELLER'

// Type for any permission string in the catalog. Extracts every leaf value
// from the P tree so `<Can permission={someVariable}>` still has type help
// when the variable is dynamic.
type Leaf<T> = T extends string ? T : T extends object ? Leaf<T[keyof T]> : never
export type Permission = Leaf<typeof P>
